import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Truck, ClipboardList, Database, LogOut, Menu, X, LayoutDashboard, Users, BellRing, Wrench, Banknote, Crown, History, ReceiptText, Package, WifiOff, CloudUpload, Download, DatabaseBackup, ShieldAlert, Scale, Weight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { canInstall, promptInstall, isIos, isStandalone } from "../offline";
import { ChangePasswordDialog } from "./ChangePasswordDialog";

const allNavItems = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard", roles: ["owner"] },
  { to: "/eksekutif", label: "Dashboard Pemilik", icon: Crown, testId: "nav-eksekutif", roles: ["owner"] },
  { to: "/trips", label: "Daftar Trip", icon: ClipboardList, testId: "nav-trips", roles: ["owner", "admin_operasional", "kasir"] },
  { to: "/tonase", label: "Input Tonase", icon: Weight, testId: "nav-tonase", roles: ["supir"] },
  { to: "/armada", label: "Armada & Servis", icon: Wrench, testId: "nav-armada", roles: ["owner"] },
  { to: "/gudang", label: "Inventaris Gudang", icon: Package, testId: "nav-gudang", roles: ["owner"] },
  { to: "/gaji", label: "Penggajian Supir", icon: Banknote, testId: "nav-gaji", roles: ["owner", "kasir"] },
  { to: "/invoice", label: "Penagihan Klien", icon: ReceiptText, testId: "nav-invoice", roles: ["owner", "kasir"] },
  { to: "/laba-rugi", label: "Laba Rugi", icon: Scale, testId: "nav-laba-rugi", roles: ["owner"] },
  { to: "/rekap-kendaraan", label: "Rekap Kendaraan", icon: Truck, testId: "nav-rekap-kendaraan", roles: ["owner"] },
  { to: "/master-data", label: "Master Data", icon: Database, testId: "nav-master-data", roles: ["owner"] },
  { to: "/users", label: "Manajemen User", icon: Users, testId: "nav-users", roles: ["owner"] },
  { to: "/audit", label: "Log Aktivitas", icon: History, testId: "nav-audit", roles: ["owner"] },
  { to: "/jejak-audit", label: "Jejak Audit", icon: ShieldAlert, testId: "nav-jejak-audit", roles: ["owner"] },
  { to: "/backup", label: "Backup & Restore", icon: DatabaseBackup, testId: "nav-backup", roles: ["owner"] },
  { to: "/notifikasi", label: "Notifikasi", icon: BellRing, testId: "nav-notifications", roles: ["owner"] },
];

const roleLabel = { owner: "Owner/Pemilik", admin_operasional: "Admin Operasional", kasir: "Kasir/Keuangan", supir: "Supir" };

const OfflineIndicator = () => {
  const [online, setOnline] = useState(navigator.onLine);
  const [count, setCount] = useState(0);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    const onQueue = (e) => setCount(e.detail.count);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    window.addEventListener("offline-queue-changed", onQueue);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
      window.removeEventListener("offline-queue-changed", onQueue);
    };
  }, []);
  if (online && count === 0) return null;
  return (
    <div data-testid="offline-indicator"
      className={`mb-5 rounded-lg px-4 py-2.5 text-sm font-medium flex items-center gap-2 border ${online ? "bg-amber-50 text-amber-800 border-amber-300" : "bg-zinc-900 text-white border-zinc-900"}`}>
      {online ? <CloudUpload className="w-4 h-4 shrink-0" /> : <WifiOff className="w-4 h-4 shrink-0" />}
      {online
        ? <span data-testid="pending-sync-label">{count} data <b>Tertunda</b> — sinkronisasi otomatis sedang berjalan...</span>
        : <span data-testid="offline-mode-label">Mode Offline — form tetap bisa diisi{count > 0 ? `. ${count} data Tertunda akan terkirim otomatis saat online` : ""}</span>}
    </div>
  );
};

const InstallButton = () => {
  const [, force] = useState(0);
  useEffect(() => {
    const onChange = () => force((n) => n + 1);
    window.addEventListener("pwa-install-changed", onChange);
    return () => window.removeEventListener("pwa-install-changed", onChange);
  }, []);
  if (isStandalone()) return null;
  const handleClick = async () => {
    if (canInstall()) {
      const outcome = await promptInstall();
      if (outcome === "accepted") toast.success("Aplikasi sedang dipasang ke layar utama...");
    } else if (isIos()) {
      toast.info('Di iPhone/iPad: ketuk tombol Bagikan (ikon kotak dengan panah ke atas) lalu pilih "Tambahkan ke Layar Utama"', { duration: 9000 });
    } else {
      toast.info('Buka menu browser (ikon titik tiga di kanan atas) lalu pilih "Tambahkan ke layar utama" / "Instal aplikasi"', { duration: 9000 });
    }
  };
  return (
    <button data-testid="install-app-button" onClick={handleClick}
      className="flex items-center gap-2 text-sm font-medium text-emerald-700 hover:text-emerald-900 transition-colors duration-200 mb-2">
      <Download className="w-4 h-4" /> Pasang Aplikasi
    </button>
  );
};

const NavLinks = ({ role, onNavigate }) => (
  <nav className="flex-1 px-3 space-y-1">
    {allNavItems.filter((i) => i.roles.includes(role)).map(({ to, label, icon: Icon, testId }) => (
      <NavLink key={to} to={to} end={to === "/"} data-testid={testId} onClick={onNavigate}
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 ${
            isActive ? "bg-emerald-700 text-white" : "text-zinc-600 hover:bg-zinc-100"
          }`
        }>
        <Icon className="w-5 h-5" />
        {label}
      </NavLink>
    ))}
  </nav>
);

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    try {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    } catch (e) {}
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-zinc-100 flex">
      <aside className="hidden md:flex w-64 flex-col bg-white border-r border-zinc-200 fixed inset-y-0">
        <div className="flex items-center gap-3 px-5 h-16 border-b border-zinc-200">
          <div className="w-9 h-9 rounded-md bg-emerald-700 flex items-center justify-center">
            <Truck className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="text-sm font-semibold tracking-tight text-zinc-900">Trip Surat Jalan</p>
            <p className="text-[11px] text-zinc-500">Manajemen Perjalanan Truk</p>
          </div>
        </div>
        <div className="py-4 flex-1 flex flex-col">
          <NavLinks role={user?.role} />
        </div>
        <div className="p-4 border-t border-zinc-200">
          <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
          <p data-testid="user-role-label" className="text-[11px] font-medium text-emerald-700 mb-2">{roleLabel[user?.role] || user?.role}</p>
          <InstallButton />
          <ChangePasswordDialog />
          <button data-testid="logout-button" onClick={handleLogout}
            className="flex items-center gap-2 text-sm text-zinc-600 hover:text-red-600 transition-colors duration-200">
            <LogOut className="w-4 h-4" /> Keluar
          </button>
        </div>
      </aside>

      <div className="flex-1 md:ml-64 flex flex-col min-w-0">
        <header className="md:hidden flex items-center justify-between bg-white border-b border-zinc-200 h-14 px-4 sticky top-0 z-20">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-emerald-700 flex items-center justify-center">
              <Truck className="w-4 h-4 text-white" />
            </div>
            <span className="text-sm font-semibold text-zinc-900">Trip Surat Jalan</span>
          </div>
          <button data-testid="mobile-menu-button" onClick={() => setMobileOpen(!mobileOpen)} className="p-2 text-zinc-600">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </header>
        {mobileOpen && (
          <div className="md:hidden bg-white border-b border-zinc-200 py-3 sticky top-14 z-20">
            <NavLinks role={user?.role} onNavigate={() => setMobileOpen(false)} />
            <div className="px-6 pt-3 space-y-2">
              <InstallButton />
              <ChangePasswordDialog mobile />
              <button data-testid="mobile-logout-button" onClick={handleLogout}
                className="flex items-center gap-2 text-sm text-zinc-600">
                <LogOut className="w-4 h-4" /> Keluar
              </button>
            </div>
          </div>
        )}
        <main className="flex-1 p-4 sm:p-6 lg:p-8"><OfflineIndicator />{children}</main>
      </div>
    </div>
  );
}
